import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  ChevronUpDownIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ArrowRightOnRectangleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

const ROLE_COLORS = {
  super_admin: "bg-purple-500/20 text-purple-300",
  owner: "bg-brand-500/20 text-brand-300",
  treatment_coordinator: "bg-emerald-500/20 text-emerald-300",
  front_desk: "bg-blue-500/20 text-blue-300",
  agent: "bg-cyan-500/20 text-cyan-300",
  team_leader: "bg-indigo-500/20 text-indigo-300",
};

const AccountSwitcher = ({ collapsed }) => {
  const {
    user,
    accounts,
    switchAccount,
    addAccount,
    removeAccount,
    logoutAll,
  } = useAuth();
  const navigate                      = useNavigate();
  const [open, setOpen]               = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding]           = useState(false);
  const [switching, setSwitching]     = useState(null);
  const [error, setError]             = useState("");
  const panelRef                      = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
        setShowAddForm(false);
        setError("");
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const initials = (first, last) =>
    `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();

  const handleSwitch = async (userId) => {
    if (userId === user?._id) {
      setOpen(false);
      return;
    }
    try {
      setSwitching(userId);
      await switchAccount(userId);
      setOpen(false);
      navigate("/dashboard");
    } catch {
      setError("Session expired. Please add the account again.");
    } finally {
      setSwitching(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    setAdding(true);
    try {
      await addAccount(email, password);
      setEmail("");
      setPassword("");
      setShowAddForm(false);
    } catch (err) {
      setError(err.error?.message || "Login failed");
    } finally {
      setAdding(false);
    }
  };

  const handleLogoutAll = async () => {
    await logoutAll();
    navigate("/login");
  };

  const activeId = user?._id;

  /** Profiles without a stored Bearer cannot be switched to (session cleared / expired). */
  const accountsWithValidSwitchToken = useMemo(
    () => (accounts || []).filter((a) => Boolean(a.token)),
    [accounts],
  );

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger — the user info card */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "w-full relative mx-0 p-3 rounded-xl",
          "bg-gradient-to-br from-dark-800/80 to-dark-800/40",
          "border border-dark-700/50 hover:border-dark-600/70 transition-colors cursor-pointer",
        )}
      >
        <div
          className={clsx(
            "flex items-center",
            collapsed ? "justify-center" : "gap-3",
          )}
        >
          {/* Avatar */}
          <div className="relative">
            <div
              className={clsx(
                "rounded-xl overflow-hidden shadow-xl ring-1 ring-white/20 flex items-center justify-center",
                collapsed ? "w-10 h-10" : "w-12 h-12",
                "bg-gradient-to-br from-slate-800 to-slate-900",
              )}
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt="avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-sm tracking-tighter">
                  {initials(user?.firstName, user?.lastName)}
                </span>
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success-500 border-2 border-slate-900" />
            </div>
          </div>

          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-semibold text-white truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-dark-400 truncate capitalize">
                  {user?.role?.replace(/_/g, " ")}
                </p>
              </div>
              <ChevronUpDownIcon
                className={clsx(
                  "w-4 h-4 text-dark-400 transition-transform",
                  open && "rotate-180",
                )}
              />
            </>
          )}
        </div>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className={clsx(
            "absolute z-[100] mt-2 rounded-xl shadow-2xl border border-dark-700/70",
            "bg-dark-900/95 backdrop-blur-xl overflow-hidden",
            collapsed ? "left-full ml-2 top-0 w-72" : "left-0 right-0",
          )}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-dark-700/50">
            <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider">
              Switch Account
            </p>
          </div>

          {/* Account list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {accountsWithValidSwitchToken.length === 0 ? (
              <p className="px-4 py-3 text-xs text-dark-500">
                No other saved sessions. Use Add Account to sign in again.
              </p>
            ) : null}
            {accountsWithValidSwitchToken.map((acct) => {
              const isActive = acct.userId === activeId;
              const isSwitching = switching === acct.userId;
              return (
                <div
                  key={acct.userId}
                  className={clsx(
                    "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors group",
                    isActive
                      ? "bg-brand-500/10"
                      : "hover:bg-dark-800/60",
                  )}
                  onClick={() => handleSwitch(acct.userId)}
                >
                  {/* Avatar */}
                  <div
                    className={clsx(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ring-1",
                      isActive
                        ? "bg-brand-500/20 ring-brand-500/40"
                        : "bg-dark-800 ring-dark-700/50",
                    )}
                  >
                    {acct.avatar ? (
                      <img
                        src={acct.avatar}
                        alt=""
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      <span className="text-xs font-bold text-white">
                        {initials(acct.firstName, acct.lastName)}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {acct.firstName} {acct.lastName}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "text-[10px] px-1.5 py-0.5 rounded-md font-medium capitalize",
                          ROLE_COLORS[acct.role] || "bg-dark-700 text-dark-300",
                        )}
                      >
                        {acct.role?.replace(/_/g, " ")}
                      </span>
                      {acct.organizationName && (
                        <span className="text-[10px] text-dark-500 truncate">
                          {acct.organizationName}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Active check / switching spinner */}
                  {isActive && (
                    <CheckIcon className="w-4 h-4 text-brand-400 shrink-0" />
                  )}
                  {isSwitching && (
                    <div className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin shrink-0" />
                  )}

                  {/* Remove button (not for active account) */}
                  {!isActive && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAccount(acct.userId);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-error-500/20 text-dark-500 hover:text-error-400 transition-all shrink-0"
                      title="Remove account"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 py-2 text-xs text-error-400 bg-error-500/10">
              {error}
            </div>
          )}

          {/* Add account form */}
          {showAddForm ? (
            <form
              onSubmit={handleAdd}
              className="px-4 py-3 border-t border-dark-700/50 space-y-2"
            >
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-dark-700 text-sm text-white placeholder-dark-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
             <div className="relative w-full"> {/* Parent must be relative */}
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 pr-10 rounded-lg bg-dark-800 border border-dark-700 text-sm text-surface-100 placeholder-dark-500 focus:outline-none focus:ring-1 focus:ring-brand-800"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-surface-100 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeIcon className="w-4 h-4 text-dark-400" />
                ) : (
                  <EyeSlashIcon className="w-4 h-4 text-dark-400" />
                )}
              </button>
            </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={adding}
                  className="flex-1 px-3 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {adding ? "Signing in..." : "Add"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setError("");
                  }}
                  className="px-3 py-2 rounded-lg bg-dark-800 hover:bg-dark-700 text-dark-300 text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="border-t border-dark-700/50">
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-dark-300 hover:text-white hover:bg-dark-800/60 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Account
              </button>
              <button
                onClick={handleLogoutAll}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-error-400 hover:bg-error-500/10 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Log Out All Accounts
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;
